import { RequestProps } from '../../types'
import { SettlementMethod } from '../../types/enums/SettlementMethod'
import { ContractLightDTO } from '../../types/models/Contract'
import { BaseResponse } from '../../types/response/BaseResponse'
import { apiSlice } from '../api/apiSlice'

export const contractSlice = apiSlice.injectEndpoints({
  endpoints: build => ({
    getEmployeeContracts: build.query<BaseResponse<ContractLightDTO>, { id: string; requestProps: RequestProps }>({
      query: ({ id, requestProps }) => ({
        url: `/contracts/employee/${id}`,
        params: requestProps,
      }),
      providesTags: () => [{ type: 'Contract', id: 'LIST' }],
    }),
    getEmployeeCurrentContract: build.query<ContractReturnDTO | undefined, { id: string }>({
      queryFn: async (arg, api, extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/contracts/employee/${arg.id}/current`,
        })
        if (result.error && result.error.status === 404) {
          return { data: undefined }
        }
        return result.data ? { data: result.data as ContractReturnDTO } : { error: result.error }
      },
      providesTags: () => [{ type: 'Contract', id: 'LIST' }],
    }),
    addContract: build.mutation<ContractReturnDTO, ContractModifyDTO>({
      query: body => ({
        url: `/contracts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: () => [{ type: 'Contract', id: 'LIST' }],
    }),
    editContract: build.mutation<ContractReturnDTO, { body: ContractModifyDTO; id: string }>({
      query: ({ id, body }) => ({
        url: `/contracts/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Contract', id },
        { type: 'Contract', id: 'LIST' },
        { type: 'Worklog', id: 'LIST' },
        { type: 'YearlySummary', id: 'LIST' },
      ],
    }),
    deleteContract: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/contracts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => [{ type: 'Contract', id: 'LIST' }],
    }),
    getContracts: build.query<BaseResponse<ContractLightDTO>, RequestProps>({
      query: props => ({
        url: `/contracts`,
        params: props,
      }),
      providesTags: () => [{ type: 'Contract', id: 'LIST' }],
    }),
    getContract: build.query<ContractReturnDTO, { id: string }>({
      query: ({ id }) => ({
        url: `/contracts/${id}`,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Contract', id }],
    }),
    getPaidVacationPeriods: build.query<
      BaseResponse<PaidVacationPeriodReturnDTO>,
      { id: string; requestProps: RequestProps }
    >({
      query: ({ id, requestProps }) => ({
        url: `/contracts/${id}/vacation-periods`,
        params: requestProps,
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Contract', id }],
    }),
    addPaidVacationPeriod: build.mutation<
      PaidVacationPeriodReturnDTO[],
      { id: string; body: PaidVacationPeriodModifyDTO }
    >({
      query: ({ id, body }) => ({
        url: `/contracts/${id}/vacation-periods`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Contract', id },
        { type: 'Contract', id: 'LIST' },
      ],
    }),
    addPaidVacationPeriods: build.mutation<
      PaidVacationPeriodReturnDTO[],
      { id: string; body: PaidVacationPeriodModifyDTO[] }
    >({
      query: ({ id, body }) => ({
        url: `/contracts/${id}/vacation-periods/bulk`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Contract', id },
        { type: 'Contract', id: 'LIST' },
      ],
    }),
    updatePaidVacationPeriod: build.mutation<
      PaidVacationPeriodReturnDTO[],
      { id: string; periodId: string; body: PaidVacationPeriodModifyDTO }
    >({
      query: ({ id, periodId, body }) => ({
        url: `/contracts/${id}/vacation-periods/${periodId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Contract', id },
        { type: 'Contract', id: 'LIST' },
      ],
    }),
    replacePaidVacationPeriods: build.mutation<
      PaidVacationPeriodReturnDTO[],
      { id: string; body: PaidVacationPeriodModifyDTO[] }
    >({
      query: ({ id, body }) => ({
        url: `/contracts/${id}/vacation-periods/bulk`,
        method: 'PUT',
        body,
      }),
      // invalidatesTags: (_result, _error, { id }) => [
      //   { type: 'Contract', id },
      //   { type: 'Contract', id: 'LIST' },
      // ],
    }),
    deletePaidVacationPeriod: build.mutation<void, { id: string; periodId: string }>({
      query: ({ id, periodId }) => ({
        url: `/contracts/${id}/vacation-periods/${periodId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Contract', id },
        { type: 'Contract', id: 'LIST' },
      ],
    }),
    deletePaidVacationPeriods: build.mutation<void, { id: string; periodIds: string[] }>({
      query: ({ id, periodIds }) => ({
        url: `/contracts/${id}/vacation-periods/bulk`,
        method: 'DELETE',
        body: periodIds,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Contract', id },
        { type: 'Contract', id: 'LIST' },
      ],
    }),
    deleteContractsBulk: build.mutation<void, { contractIds: string[] }>({
      query: ({ contractIds }) => ({
        url: `/contracts/bulk`,
        method: 'DELETE',
        body: contractIds,
      }),
      invalidatesTags: (_result, _error) => [{ type: 'Contract', id: 'LIST' }],
    }),
  }),
})

type EmployeeFullNameDTO = {
  id: string
  firstName: string
  lastName: string
}

export type PaidVacationPeriodModifyDTO = {
  id: string | undefined
  vacationDays: number
  overtimeHours: number
  version: number | null
  startDate: string
  endDate: string
}

export type PaidVacationPeriodReturnDTO = {
  id: string
  vacationDays: number
  startDate: string
  endDate: string
  overtimeHours: number
  version: number | null
  createdAt: string
  modifiedAt: string
  createdBy: EmployeeFullNameDTO
  editedBy: EmployeeFullNameDTO
}

type ContractModifyDTO = {
  employeeId: string
  contractTypeId: string
  startDate: string
  indefiniteDuration: boolean
  endDate: string | null
  settlementMethod: SettlementMethod
  employmentPercentage: number
  paidVacation?: boolean
  version: number | null
  paidVacationPeriods: PaidVacationPeriodModifyDTO[]
}

type ContractReturnDTO = {
  id: string
  employeeId: string
  employeeFullName: string
  contractTypeId: string
  contractTypeValue: string
  startDate: string
  endDate: string
  indefiniteDuration?: boolean
  settlementMethod: SettlementMethod
  employmentPercentage: number
  paidVacation: boolean
  version: number | null
  createdAt: string
  modifiedAt: string
  paidVacationPeriods: PaidVacationPeriodReturnDTO[]
}

export const {
  useGetEmployeeContractsQuery,
  useGetEmployeeCurrentContractQuery,
  useAddContractMutation,
  useEditContractMutation,
  useDeleteContractMutation,
  useGetContractsQuery,
  useGetContractQuery,
  useGetPaidVacationPeriodsQuery,
  useAddPaidVacationPeriodMutation,
  useAddPaidVacationPeriodsMutation,
  useUpdatePaidVacationPeriodMutation,
  useReplacePaidVacationPeriodsMutation,
  useDeletePaidVacationPeriodMutation,
  useDeletePaidVacationPeriodsMutation,
  useDeleteContractsBulkMutation,
} = contractSlice
